import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import {
  Plus, X, EnvelopeSimple, ChatText, PaperPlaneTilt, Upload, Gear, Star, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ReviewRequests() {
  const { activeBiz } = useOutletContext();
  const [tab, setTab] = useState("send");
  const [customers, setCustomers] = useState([]);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [channel, setChannel] = useState("both");
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const reload = async () => {
    if (!activeBiz) return;
    try {
      const [c, h, s] = await Promise.all([
        api.get(`/businesses/${activeBiz.id}/customers`),
        api.get(`/businesses/${activeBiz.id}/review-requests`),
        api.get(`/businesses/${activeBiz.id}/review-settings`),
      ]);
      setCustomers(c.data);
      setHistory(h.data);
      setSettings(s.data.review_settings);
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeBiz]);

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === customers.length) setSelected(new Set());
    else setSelected(new Set(customers.map((c) => c.id)));
  };

  const addCust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/businesses/${activeBiz.id}/customers`, form);
      setForm({ name: "", email: "", phone: "" });
      setShowAdd(false);
      reload();
      toast.success("Customer added");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const importCsv = async () => {
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/customers/import`, { csv_text: csvText });
      toast.success(`Imported ${data.added}`);
      setShowImport(false); setCsvText("");
      reload();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const removeCust = async (id) => {
    try { await api.delete(`/businesses/${activeBiz.id}/customers/${id}`); reload(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const send = async () => {
    if (!selected.size) return toast.error("Select customers first");
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/review-requests/send`, {
        customer_ids: Array.from(selected),
        channel,
      });
      const mockNote = data.mocked ? " (MOCKED — add API keys to send for real)" : "";
      toast.success(`Sent: ${data.sent_email} email · ${data.sent_sms} SMS${mockNote}`);
      setSelected(new Set());
      reload();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put(`/businesses/${activeBiz.id}/review-settings`, patch);
      setSettings(data.review_settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submittedCount = history.filter((h) => h.submitted_at).length;
  const avgRating = history.filter((h) => h.rating).reduce((s, h, _, a) => s + h.rating / a.length, 0).toFixed(1);

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-7xl">
      <header>
        <div className="label-tiny mb-1">Review Requests</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="rr-title">Get more 5-star reviews</h1>
        <p className="text-zinc-500 mt-1">Email + SMS automation. Review velocity is one of Google's strongest ranking signals.</p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Customers" value={customers.length} testid="rr-stat-customers" />
        <Stat label="Requests Sent" value={history.length} testid="rr-stat-sent" />
        <Stat label="Reviews Submitted" value={submittedCount} testid="rr-stat-submitted" />
        <Stat label="Avg Rating" value={isNaN(parseFloat(avgRating)) ? "—" : avgRating} testid="rr-stat-rating" />
      </div>

      <div className="flex gap-px bg-zinc-200 border border-zinc-200 w-fit" data-testid="rr-tabs">
        {[
          { id: "send", label: "Send Requests", Icon: PaperPlaneTilt },
          { id: "history", label: "History", Icon: ChatText },
          { id: "settings", label: "Settings", Icon: Gear },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-xs uppercase tracking-widest font-bold flex items-center gap-2 ${tab === t.id ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`} data-testid={`rr-tab-${t.id}`}>
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "send" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <button className="btn-dark !py-2 !text-[11px]" onClick={() => setShowAdd(!showAdd)} data-testid="rr-add-toggle"><Plus size={12} weight="bold" /> Add customer</button>
            <button className="btn-secondary !py-2 !text-[11px]" onClick={() => setShowImport(!showImport)} data-testid="rr-import-toggle"><Upload size={12} weight="bold" /> Import CSV</button>
            <div className="ml-auto flex items-center gap-2">
              <span className="label-tiny">Channel:</span>
              {["email", "sms", "both"].map((c) => (
                <button key={c} onClick={() => setChannel(c)} className={`px-3 py-1.5 text-xs font-mono border ${channel === c ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200"}`} data-testid={`rr-channel-${c}`}>{c}</button>
              ))}
            </div>
            <button className="btn-primary !py-2 !text-[11px]" onClick={send} disabled={busy || !selected.size} data-testid="rr-send-btn">
              {busy ? <span className="spinner"></span> : <><PaperPlaneTilt size={12} weight="bold" /> Send to {selected.size}</>}
            </button>
          </div>

          {showAdd && (
            <form onSubmit={addCust} className="bento p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="rr-add-form">
              <input className="input-field" placeholder="Name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className="input-field" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className="input-field" placeholder="Phone (+15551234567)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <button type="submit" className="btn-primary justify-center" data-testid="rr-add-submit">Add</button>
            </form>
          )}
          {showImport && (
            <div className="bento p-4 space-y-3" data-testid="rr-import-form">
              <div className="label-tiny">Paste CSV (header optional)</div>
              <textarea className="input-field font-mono text-xs" rows={6} placeholder={"name,email,phone\nAva Smith,ava@example.com,+15551112222\nMark Lin,,+15553334444"} value={csvText} onChange={(e) => setCsvText(e.target.value)} data-testid="rr-csv-text" />
              <button className="btn-primary !py-2 !text-[11px]" onClick={importCsv} disabled={!csvText.trim()} data-testid="rr-csv-import">Import</button>
            </div>
          )}

          <div className="bento p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="p-3 text-left w-10"><input type="checkbox" checked={selected.size === customers.length && customers.length > 0} onChange={toggleAll} data-testid="rr-select-all" /></th>
                  <th className="label-tiny p-3 text-left">Name</th>
                  <th className="label-tiny p-3 text-left">Email</th>
                  <th className="label-tiny p-3 text-left">Phone</th>
                  <th className="label-tiny p-3 text-left">Last sent</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-zinc-400">No customers yet. Add one or import a CSV.</td></tr>
                )}
                {customers.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`rr-cust-${c.id}`}>
                    <td className="p-3"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} data-testid={`rr-select-${c.id}`} /></td>
                    <td className="p-3 font-medium">{c.name}</td>
                    <td className="p-3 font-mono text-xs">{c.email || "—"}</td>
                    <td className="p-3 font-mono text-xs">{c.phone || "—"}</td>
                    <td className="p-3 font-mono text-xs text-zinc-500">{c.last_request_sent_at ? new Date(c.last_request_sent_at).toLocaleDateString() : "Never"}</td>
                    <td className="p-3 text-right"><button onClick={() => removeCust(c.id)} className="text-zinc-400 hover:text-zinc-900" data-testid={`rr-del-${c.id}`}><X size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(!RESEND_AVAILABLE || !TWILIO_AVAILABLE) && (
            <div className="bento p-4 border-l-4 border-[#FACC15] bg-yellow-50 text-sm">
              <strong>Mock mode:</strong> Email/SMS keys aren't configured yet. Sends are logged but not delivered. Add <code className="font-mono">RESEND_API_KEY</code> and Twilio credentials to flip to live.
            </div>
          )}
        </div>
      )}

      {tab === "history" && (
        <div className="bento p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="label-tiny p-3 text-left">Customer</th>
                <th className="label-tiny p-3 text-left">Channel</th>
                <th className="label-tiny p-3 text-left">Sent</th>
                <th className="label-tiny p-3 text-left">Submitted</th>
                <th className="label-tiny p-3 text-left">Rating</th>
                <th className="label-tiny p-3 text-left">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (<tr><td colSpan={6} className="p-8 text-center text-zinc-400">No requests sent yet.</td></tr>)}
              {history.map((h) => (
                <tr key={h.id} className="border-b border-zinc-100" data-testid={`rr-hist-${h.id}`}>
                  <td className="p-3 font-medium">{h.customer_name}</td>
                  <td className="p-3 font-mono text-xs">{h.channel}</td>
                  <td className="p-3 font-mono text-xs text-zinc-500">{new Date(h.created_at).toLocaleDateString()}</td>
                  <td className="p-3 font-mono text-xs">{h.submitted_at ? new Date(h.submitted_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3"><Stars rating={h.rating} /></td>
                  <td className="p-3 font-mono text-xs">
                    {h.redirected_to === "public_review" ? <span className="text-[#22C55E]">→ public review</span> : h.redirected_to === "private_feedback" ? <span className="text-[#EAB308]">→ private feedback</span> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "settings" && settings && (
        <SettingsPanel settings={settings} onSave={saveSettings} />
      )}
    </div>
  );
}

const RESEND_AVAILABLE = false; // shown to surface mock-mode warning until keys provided server-side
const TWILIO_AVAILABLE = false;

function Stat({ label, value, testid }) {
  return (
    <div className="bento p-4" data-testid={testid}>
      <div className="label-tiny">{label}</div>
      <div className="font-display text-3xl font-black mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Stars({ rating }) {
  if (!rating) return <span className="text-zinc-400 text-xs">—</span>;
  return (
    <span className="flex">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} size={12} weight={s <= rating ? "fill" : "regular"} className={s <= rating ? "text-[#EAB308]" : "text-zinc-300"} />
      ))}
    </span>
  );
}

function SettingsPanel({ settings, onSave }) {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);

  return (
    <div className="space-y-6">
      <div className="bento p-6 space-y-3">
        <h3 className="font-display text-xl font-bold">RankTrust Review Integrity</h3>
        <p className="text-zinc-600 text-sm">RankTrust Reviews does not use star gating. Every rating follows the same review form and moderation process.</p>
        <p className="text-xs text-zinc-500 font-mono">Businesses can dispute policy violations, but cannot suppress a review because it is negative.</p>
      </div>

      <div className="bento p-6 space-y-4">
        <h3 className="font-display text-xl font-bold">Public Review Platform Links</h3>
        <p className="text-zinc-600 text-sm">Paste your optional "leave a review" URLs. The same links are offered to every reviewer after submitting, regardless of rating.</p>
        <Field label="Google review URL"><input className="input-field" placeholder="https://g.page/r/..." value={s.google_review_url || ""} onChange={(e) => setS({ ...s, google_review_url: e.target.value })} data-testid="rr-google-url" /></Field>
        <Field label="Facebook review URL"><input className="input-field" placeholder="https://www.facebook.com/.../reviews/" value={s.facebook_review_url || ""} onChange={(e) => setS({ ...s, facebook_review_url: e.target.value })} data-testid="rr-fb-url" /></Field>
        <Field label="Yelp review URL"><input className="input-field" placeholder="https://www.yelp.com/writeareview/biz/..." value={s.yelp_review_url || ""} onChange={(e) => setS({ ...s, yelp_review_url: e.target.value })} data-testid="rr-yelp-url" /></Field>
      </div>

      <div className="bento p-6 space-y-4">
        <h3 className="font-display text-xl font-bold">Message Templates</h3>
        <p className="text-zinc-600 text-sm">Use placeholders: <code className="font-mono">{`{customer_name}`}</code>, <code className="font-mono">{`{business_name}`}</code>, <code className="font-mono">{`{review_link}`}</code></p>
        <Field label="Email subject"><input className="input-field" value={s.email_subject || ""} onChange={(e) => setS({ ...s, email_subject: e.target.value })} data-testid="rr-email-subj" /></Field>
        <Field label="Email body"><textarea className="input-field font-mono text-xs" rows={6} value={s.email_template || ""} onChange={(e) => setS({ ...s, email_template: e.target.value })} data-testid="rr-email-body" /></Field>
        <Field label="SMS body"><textarea className="input-field font-mono text-xs" rows={3} value={s.sms_template || ""} onChange={(e) => setS({ ...s, sms_template: e.target.value })} data-testid="rr-sms-body" /></Field>
      </div>

      <button className="btn-primary" onClick={() => onSave(s)} data-testid="rr-save-settings">Save settings</button>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block space-y-2"><span className="label-tiny block">{label}</span>{children}</label>;
}
